import React, { createContext, useContext, useState, useEffect } from 'react';

// Importar los archivos de traducciones
import en from '../locales/en.json';
import es from '../locales/es.json';

// Tipos para las traducciones
type Locale = 'en' | 'es';

interface Translations {
  [key: string]: any;
}

interface LocaleContextType {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: string) => any;
  translations: Translations;
}

// Traducciones disponibles
const translations: Record<Locale, Translations> = {
  en,
  es,
};

// Crear el contexto
const LocaleContext = createContext<LocaleContextType | undefined>(undefined);

// Función helper para obtener valor anidado del objeto
const getNestedValue = (obj: any, path: string): any => {
  return path.split('.').reduce((current, key) => current?.[key], obj) || path;
};

// Proveedor del contexto
export const LocaleProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [locale, setLocaleState] = useState<Locale>('es'); // Español por defecto

  // Función para cambiar idioma
  const setLocale = (newLocale: Locale) => {
    setLocaleState(newLocale);
    localStorage.setItem('preferred-locale', newLocale);
  };

  // Función de traducción
  const t = (key: string): any => {
    return getNestedValue(translations[locale], key);
  };

  // Cargar idioma guardado al inicializar
  useEffect(() => {
    const savedLocale = localStorage.getItem('preferred-locale') as Locale;
    if (savedLocale && translations[savedLocale]) {
      setLocaleState(savedLocale);
    }
  }, []);

  const value: LocaleContextType = {
    locale,
    setLocale,
    t,
    translations: translations[locale],
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

// Hook personalizado para usar el contexto
export const useLocale = (): LocaleContextType => {
  const context = useContext(LocaleContext);
  if (!context) {
    throw new Error('useLocale must be used within a LocaleProvider');
  }
  return context;
};

// Hook simplificado solo para traducciones
export const useTranslation = () => {
  const { t } = useLocale();
  return { t };
};
